import { csv } from '../RmMonitor/liveModel';

export const PAGE_ID = 'marketplace-monitoring';
export const VIEWS = ['Overview LPC (Non SPP)', 'Overview SPP', 'Recent Launches', 'Worst Performers'] as const;
export type ViewId = (typeof VIEWS)[number];

export const LPC_FACTS = 'lpc-facts';
export const LPC_WEEKS = 'lpc-weeks';
export const SPP_FACTS = 'spp-facts';
export const SPP_WEEKS = 'spp-weeks';

export const DEFAULT_PRODUCT = 'Lux Premium Collection (Non-SPP)';
export const LTLE = 'Limited Time Lux Exclusive';
export const SPLITS = ['Vendors', 'Offer Countries', 'Chains', '-'] as const;
export type Split = (typeof SPLITS)[number];
export type MarginMode = 'Exclude' | 'Include';
export type Choice = 'all' | string[];

export const VIEWS_SPP_CHANNEL = 'LPC';
export const RECENT_DAYS = { min: 0, max: 60 };
export const WORST_LPC_VIEWS = { min: 200, max: 24065 };
export const WORST_SPP_IMPRESSIONS = { min: 200, max: 16769 };
export const SPP_BRAND = 'Luxury Escapes';

export type LpcFact = {
  vendor: string | null;
  parent: string | null;
  country: string | null;
  product: string | null;
  latestId: string | null;
  planned: string | null;
  gmLw: number;
  viewsLw: number;
  gmP4: number;
  viewsP4: number | null;
  gmMpv: number;
  viewsMpv: number;
  gmSince: number;
  viewsSince: number;
  countryLpcGm: number;
  countryLpcViews: number;
};

export type LpcWeek = {
  vendor: string | null;
  country: string | null;
  product: string | null;
  week: string;
  gm: number;
  views: number;
};

export type SppFact = {
  property: string | null;
  channel: string | null;
  detail: string | null;
  country: string | null;
  parent: string | null;
  vendor: string | null;
  latestId: string | null;
  planned: string | null;
  brand: string | null;
  eligible: boolean;
  gmLw: number;
  gmMaxLw: number;
  gmP4: number;
  gmMaxP4: number;
  gmMpv: number;
  gmMaxMpv: number;
  impLw: number;
  impP4: number | null;
  impMpv: number;
  viewsLw: number;
  viewsP4: number | null;
  gmSince: number;
  gmMaxSince: number;
  impSince: number;
};

export type SppWeek = {
  property: string | null;
  channel: string | null;
  country: string | null;
  week: string;
  gm: number;
  gmMax: number;
  impressions: number;
  views: number;
};

export type Range = { min: number; max: number };

export type LpcScope = {
  split: Split;
  product: Choice;
  country: Choice;
  chain: Choice;
  vendor: Choice;
  action: string | null;
};

export type SppScope = {
  split: Split;
  margin: MarginMode;
  country: Choice;
  channel: Choice;
  detail: Choice;
  chain: Choice;
  property: Choice;
  action: string | null;
  eligibleOnly: boolean;
  brand: string | null;
};

export type UpliftRow = {
  split: string;
  mpvVs: number | null;
  viewsVs: number | null;
  gmVs: number;
  gmLw: number;
};

export type ChannelMetrics = {
  mppoiVs: number | null;
  impVs: number | null;
  viewsVs: number | null;
  marginVs: number;
  gmLw: number;
};

export type SppUpliftRow = { split: string; channels: Record<string, ChannelMetrics> };

export type RecentLpcRow = {
  days: number | null;
  planned: string | null;
  chain: string | null;
  vendor: string | null;
  country: string | null;
  latestId: string | null;
  mpvLw: number | null;
  gmLw: number;
  viewsLw: number;
  mpvSince: number | null;
  gmSince: number;
  viewsSince: number;
};

export type RecentSppRow = {
  days: number | null;
  planned: string | null;
  chain: string | null;
  property: string | null;
  country: string | null;
  latestId: string | null;
  mppoiLw: number | null;
  gmLw: number;
  impressionsLw: number;
  impressionsSince: number;
};

export type WorstLpcRow = {
  vendor: string | null;
  country: string | null;
  latestId: string | null;
  planned: string | null;
  mpvLw: number | null;
  mpvVs: number | null;
  mpvDiff: number | null;
  viewsLw: number;
  viewsVs: number | null;
  gmLw: number;
  gmVs: number;
};

export type WorstSppRow = {
  property: string | null;
  country: string | null;
  latestId: string | null;
  planned: string | null;
  mppoiLw: number | null;
  mppoiVs: number | null;
  impressionsLw: number;
  impVs: number | null;
  gmLw: number;
  gmVs: number;
};

export type Point = { week: string; value: number | null };
export type Series = { name: string; points: Point[] };

type Parts = {
  gmLw: number;
  viewsLw: number;
  gmP4: number;
  viewsP4: number | null;
  gmMpv: number;
  viewsMpv: number;
};

function unwrap(value: unknown): unknown {
  if (value && typeof value === 'object' && 'value' in value) return (value as { value: unknown }).value;
  return value;
}

function num(value: unknown): number | null {
  const raw = unwrap(value);
  if (raw === null || raw === undefined || raw === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function req(value: unknown): number {
  return num(value) ?? 0;
}

function str(value: unknown): string | null {
  const raw = unwrap(value);
  if (raw === null || raw === undefined || raw === '') return null;
  return String(raw);
}

function flag(value: unknown): boolean {
  const raw = unwrap(value);
  return raw === true || raw === 'true';
}

export function unpack(rows: ReadonlyArray<Record<string, unknown>>): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  rows.forEach((row) => {
    const payload = str(row.payload);
    if (!payload) return;
    const parsed = JSON.parse(payload) as unknown;
    if (!Array.isArray(parsed)) return;
    parsed.forEach((item) => {
      if (item && typeof item === 'object') out.push(item as Record<string, unknown>);
    });
  });
  return out;
}

export function readLpcFacts(rows: ReadonlyArray<Record<string, unknown>>): LpcFact[] {
  return unpack(rows).map((row) => ({
    vendor: str(row.v),
    parent: str(row.p),
    country: str(row.c),
    product: str(row.n),
    latestId: str(row.i),
    planned: str(row.d),
    gmLw: req(row.gl),
    viewsLw: req(row.vl),
    gmP4: req(row.gp),
    viewsP4: num(row.vp),
    gmMpv: req(row.gm),
    viewsMpv: req(row.vm),
    gmSince: req(row.gs),
    viewsSince: req(row.vs),
    countryLpcGm: req(row.cg),
    countryLpcViews: req(row.cv),
  }));
}

export function readLpcWeeks(rows: ReadonlyArray<Record<string, unknown>>): LpcWeek[] {
  return unpack(rows).map((row) => ({
    vendor: str(row.v),
    country: str(row.c),
    product: str(row.n),
    week: str(row.w) ?? '',
    gm: req(row.g),
    views: req(row.u),
  }));
}

export function readSppFacts(rows: ReadonlyArray<Record<string, unknown>>): SppFact[] {
  return unpack(rows).map((row) => ({
    property: str(row.property),
    channel: str(row.channel),
    detail: str(row.detail),
    country: str(row.country),
    parent: str(row.parent),
    vendor: str(row.vendor),
    latestId: str(row.latest_id),
    planned: str(row.planned),
    brand: str(row.brand),
    eligible: flag(row.eligible),
    gmLw: req(row.gl),
    gmMaxLw: req(row.xl),
    gmP4: req(row.gp),
    gmMaxP4: req(row.xp),
    gmMpv: req(row.gm),
    gmMaxMpv: req(row.xm),
    impLw: req(row.il),
    impP4: num(row.ip),
    impMpv: req(row.im),
    viewsLw: req(row.vl),
    viewsP4: num(row.vp),
    gmSince: req(row.gs),
    gmMaxSince: req(row.xs),
    impSince: req(row.iss),
  }));
}

export function readSppWeeks(rows: ReadonlyArray<Record<string, unknown>>): SppWeek[] {
  return unpack(rows).map((row) => ({
    property: str(row.p),
    channel: str(row.h),
    country: str(row.c),
    week: str(row.w) ?? '',
    gm: req(row.g),
    gmMax: req(row.x),
    impressions: req(row.i),
    views: req(row.v),
  }));
}

export function addDays(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function mondayOnOrBefore(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const dow = date.getUTCDay();
  return addDays(iso, dow === 0 ? -6 : 1 - dow);
}

export function weekWindows(today: string) {
  const thisWeek = mondayOnOrBefore(today);
  return { thisWeek, lastWeek: addDays(thisWeek, -7), p4Start: addDays(thisWeek, -35), mpvStart: addDays(thisWeek, -28) };
}

export function sydneyToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export function chainName(parent: string | null): string | null {
  if (!parent) return null;
  return parent.startsWith('Accor') ? 'Accor' : parent;
}

export function matches(choice: Choice, value: string | null): boolean {
  if (choice === 'all') return true;
  return value !== null && choice.includes(value);
}

export function splitOf(split: Split, vendor: string | null, country: string | null, parent: string | null): string | null {
  if (split === '-') return null;
  if (split === 'Vendors') return vendor;
  if (split === 'Offer Countries') return country;
  return parent;
}

export function daysSince(planned: string | null, today: string): number | null {
  if (!planned || !/^\d{4}-\d{2}-\d{2}/.test(planned)) return null;
  const start = Date.parse(`${planned.slice(0, 10)}T00:00:00Z`);
  const end = Date.parse(`${today}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.round((end - start) / 86400000);
}

export function inRange(value: number | null, range: Range): boolean {
  return value !== null && value >= range.min && value <= range.max;
}

function addNullable(left: number | null, right: number | null): number | null {
  if (left === null) return right;
  if (right === null) return left;
  return left + right;
}

function ratio(num: number | null, den: number | null): number | null {
  if (num === null || den === null || den === 0) return null;
  return num / den;
}

export function lpcMetrics(part: Parts) {
  const mpvLw = ratio(part.gmLw, part.viewsLw);
  const mpvP4 = ratio(part.gmMpv, part.viewsMpv);
  const viewsP4 = part.viewsP4 === null ? null : part.viewsP4 / 4;
  return {
    mpvVs: mpvLw === null || mpvP4 === null || mpvP4 === 0 ? null : mpvLw / mpvP4 - 1,
    viewsVs: viewsP4 === null || viewsP4 === 0 ? null : part.viewsLw / viewsP4 - 1,
    gmVs: part.gmLw - part.gmP4 / 4,
    gmLw: part.gmLw,
    mpvLw,
    viewsLw: part.viewsLw,
  };
}

export function marginGm(fact: SppFact, margin: MarginMode): number {
  return margin === 'Exclude' ? fact.gmLw - fact.gmMaxLw : fact.gmLw;
}

export function marginMpv(fact: SppFact, margin: MarginMode): number {
  return margin === 'Exclude' ? fact.gmMpv - fact.gmMaxMpv : fact.gmMpv;
}

export function marginP4(fact: SppFact, margin: MarginMode): number {
  return margin === 'Exclude' ? fact.gmP4 - fact.gmMaxP4 : fact.gmP4;
}

export function sppChannelMetrics(part: {
  gmLw: number;
  gmP4: number;
  marginLw: number;
  marginMpv: number;
  impLw: number;
  impP4: number | null;
  impMpv: number;
  viewsLw: number;
  viewsP4: number | null;
}): ChannelMetrics {
  const mppoiLw = ratio(part.marginLw, part.impLw);
  const mpiP4 = ratio(part.marginMpv, part.impMpv);
  const impP4 = part.impP4 === null ? null : part.impP4 / 4;
  const viewsP4 = part.viewsP4 === null ? null : part.viewsP4 / 4;
  return {
    mppoiVs: mppoiLw === null || mpiP4 === null || mpiP4 === 0 ? null : mppoiLw / mpiP4 - 1,
    impVs: impP4 === null || impP4 === 0 ? null : part.impLw / impP4 - 1,
    viewsVs: viewsP4 === null || viewsP4 === 0 ? null : part.viewsLw / viewsP4 - 1,
    marginVs: part.gmLw - part.gmP4 / 4,
    gmLw: part.gmLw,
  };
}

function byGmThenName(name: (row: { split: string; gmLw: number }) => string) {
  return (a: { split: string; gmLw: number }, b: { split: string; gmLw: number }) =>
    b.gmLw - a.gmLw || name(a).localeCompare(name(b));
}

export function lpcUplift(facts: ReadonlyArray<LpcFact>, scope: LpcScope): { rows: UpliftRow[]; grand: UpliftRow } {
  const grouped = new Map<string, Parts>();
  facts.forEach((fact) => {
    if (!matches(scope.product, fact.product)) return;
    if (!matches(scope.country, fact.country)) return;
    if (!matches(scope.chain, chainName(fact.parent))) return;
    if (!matches(scope.vendor, fact.vendor)) return;
    const split = splitOf(scope.split, fact.vendor, fact.country, fact.parent);
    if (split === null) return;
    const acc = grouped.get(split) ?? { gmLw: 0, viewsLw: 0, gmP4: 0, viewsP4: null, gmMpv: 0, viewsMpv: 0 };
    acc.gmLw += fact.gmLw;
    acc.viewsLw += fact.viewsLw;
    acc.gmP4 += fact.gmP4;
    acc.viewsP4 = addNullable(acc.viewsP4, fact.viewsP4);
    acc.gmMpv += fact.gmMpv;
    acc.viewsMpv += fact.viewsMpv;
    grouped.set(split, acc);
  });
  const rows = [...grouped.entries()]
    .map(([split, part]) => ({ split, ...lpcMetrics(part) }))
    .sort(byGmThenName((row) => row.split));
  const total: Parts = { gmLw: 0, viewsLw: 0, gmP4: 0, viewsP4: null, gmMpv: 0, viewsMpv: 0 };
  grouped.forEach((part) => {
    total.gmLw += part.gmLw;
    total.viewsLw += part.viewsLw;
    total.gmP4 += part.gmP4;
    total.viewsP4 = addNullable(total.viewsP4, part.viewsP4);
    total.gmMpv += part.gmMpv;
    total.viewsMpv += part.viewsMpv;
  });
  return { rows, grand: { split: 'Grand Total', ...lpcMetrics(total) } };
}

type SppAcc = {
  gmLw: number;
  gmP4: number;
  marginLw: number;
  marginMpv: number;
  impLw: number;
  impP4: number | null;
  impMpv: number;
  viewsLw: number;
  viewsP4: number | null;
};

function emptySpp(): SppAcc {
  return { gmLw: 0, gmP4: 0, marginLw: 0, marginMpv: 0, impLw: 0, impP4: null, impMpv: 0, viewsLw: 0, viewsP4: null };
}

function addSpp(acc: SppAcc, fact: SppFact, margin: MarginMode) {
  acc.gmLw += fact.gmLw;
  acc.gmP4 += fact.gmP4;
  acc.marginLw += marginGm(fact, margin);
  acc.marginMpv += marginMpv(fact, margin);
  acc.impLw += fact.impLw;
  acc.impP4 = addNullable(acc.impP4, fact.impP4);
  acc.impMpv += fact.impMpv;
  acc.viewsLw += fact.viewsLw;
  acc.viewsP4 = addNullable(acc.viewsP4, fact.viewsP4);
}

function sppVisible(fact: SppFact, scope: SppScope): boolean {
  if (scope.eligibleOnly && !fact.eligible) return false;
  if (scope.brand && fact.brand !== scope.brand) return false;
  if (!matches(scope.country, fact.country)) return false;
  if (!matches(scope.channel, fact.channel)) return false;
  if (!matches(scope.detail, fact.detail)) return false;
  if (!matches(scope.chain, chainName(fact.parent))) return false;
  if (!matches(scope.property, fact.property)) return false;
  return true;
}

export function sppUplift(facts: ReadonlyArray<SppFact>, scope: SppScope): { rows: SppUpliftRow[]; channels: string[]; grand: Record<string, ChannelMetrics> } {
  const grouped = new Map<string, Map<string, SppAcc>>();
  facts.forEach((fact) => {
    if (!sppVisible(fact, scope)) return;
    const split = splitOf(scope.split, fact.property, fact.country, fact.parent);
    if (split === null || fact.channel === null) return;
    const byChannel = grouped.get(split) ?? new Map<string, SppAcc>();
    const acc = byChannel.get(fact.channel) ?? emptySpp();
    addSpp(acc, fact, scope.margin);
    byChannel.set(fact.channel, acc);
    grouped.set(split, byChannel);
  });
  const channels = [...new Set([...grouped.values()].flatMap((map) => [...map.keys()]))].sort((a, b) => a.localeCompare(b));
  const rows = [...grouped.entries()]
    .map(([split, byChannel]) => {
      const channels: Record<string, ChannelMetrics> = {};
      byChannel.forEach((part, channel) => {
        channels[channel] = sppChannelMetrics(part);
      });
      return { split, channels };
    })
    .sort((a, b) => {
      const gm = (row: SppUpliftRow) => Object.values(row.channels).reduce((sum, cell) => sum + cell.gmLw, 0);
      return gm(b) - gm(a) || a.split.localeCompare(b.split);
    });
  const grandAcc = new Map<string, SppAcc>();
  grouped.forEach((byChannel) => {
    byChannel.forEach((part, channel) => {
      const acc = grandAcc.get(channel) ?? emptySpp();
      acc.gmLw += part.gmLw;
      acc.gmP4 += part.gmP4;
      acc.marginLw += part.marginLw;
      acc.marginMpv += part.marginMpv;
      acc.impLw += part.impLw;
      acc.impP4 = addNullable(acc.impP4, part.impP4);
      acc.impMpv += part.impMpv;
      acc.viewsLw += part.viewsLw;
      acc.viewsP4 = addNullable(acc.viewsP4, part.viewsP4);
      grandAcc.set(channel, acc);
    });
  });
  const grand: Record<string, ChannelMetrics> = {};
  grandAcc.forEach((part, channel) => {
    grand[channel] = sppChannelMetrics(part);
  });
  return { rows, channels, grand };
}

function compareText(a: string | null, b: string | null): number {
  return (a ?? '').localeCompare(b ?? '');
}

function sortByOuterMeasure<T>(rows: T[], outer: (row: T) => string | null, measure: (row: T) => number, within: (a: T, b: T) => number): T[] {
  const totals = new Map<string, number>();
  rows.forEach((row) => {
    const key = outer(row) ?? '';
    totals.set(key, (totals.get(key) ?? 0) + measure(row));
  });
  return rows.sort((a, b) => {
    const delta = (totals.get(outer(a) ?? '') ?? 0) - (totals.get(outer(b) ?? '') ?? 0);
    return delta || compareText(outer(a), outer(b)) || within(a, b);
  });
}

export function recentLpc(facts: ReadonlyArray<LpcFact>, scope: LpcScope, today: string, days: Range): RecentLpcRow[] {
  const grouped = new Map<string, LpcFact & { gmLw: number; viewsLw: number; gmSince: number; viewsSince: number; gmMpv: number; viewsMpv: number }>();
  facts.forEach((fact) => {
    if (fact.product !== DEFAULT_PRODUCT) return;
    if (!matches(scope.country, fact.country)) return;
    if (!matches(scope.chain, chainName(fact.parent))) return;
    if (!matches(scope.vendor, fact.vendor)) return;
    const age = daysSince(fact.planned, today);
    if (!inRange(age, days)) return;
    const key = [fact.planned, fact.parent, fact.vendor, fact.country, fact.latestId].join('|');
    const acc = grouped.get(key);
    if (!acc) grouped.set(key, { ...fact });
    else {
      acc.gmLw += fact.gmLw;
      acc.viewsLw += fact.viewsLw;
      acc.gmSince += fact.gmSince;
      acc.viewsSince += fact.viewsSince;
    }
  });
  return [...grouped.values()]
    .map((fact) => ({
      days: daysSince(fact.planned, today),
      planned: fact.planned,
      chain: fact.parent,
      vendor: fact.vendor,
      country: fact.country,
      latestId: fact.latestId,
      mpvLw: ratio(fact.gmLw, fact.viewsLw),
      gmLw: fact.gmLw,
      viewsLw: fact.viewsLw,
      mpvSince: ratio(fact.gmSince, fact.viewsSince),
      gmSince: fact.gmSince,
      viewsSince: fact.viewsSince,
    }))
    .sort(
      (a, b) =>
        (a.days ?? 99999) - (b.days ?? 99999) ||
        compareText(a.planned, b.planned) ||
        compareText(a.chain, b.chain) ||
        compareText(a.vendor, b.vendor) ||
        compareText(a.country, b.country) ||
        compareText(a.latestId, b.latestId),
    );
}

export function recentSpp(facts: ReadonlyArray<SppFact>, scope: SppScope, today: string, days: Range): RecentSppRow[] {
  const grouped = new Map<string, SppAcc & { planned: string | null; chain: string | null; property: string | null; country: string | null; latestId: string | null; impSince: number }>();
  facts.forEach((fact) => {
    if (!sppVisible(fact, { ...scope, eligibleOnly: false })) return;
    const age = daysSince(fact.planned, today);
    if (!inRange(age, days)) return;
    const key = [fact.planned, fact.parent, fact.property, fact.country, fact.latestId].join('|');
    const acc = grouped.get(key) ?? { ...emptySpp(), planned: fact.planned, chain: fact.parent, property: fact.property, country: fact.country, latestId: fact.latestId, impSince: 0 };
    addSpp(acc, fact, scope.margin);
    acc.impSince += fact.impSince;
    grouped.set(key, acc);
  });
  return [...grouped.values()]
    .map((fact) => ({
      days: daysSince(fact.planned, today),
      planned: fact.planned,
      chain: fact.chain,
      property: fact.property,
      country: fact.country,
      latestId: fact.latestId,
      mppoiLw: ratio(fact.marginLw, fact.impLw),
      gmLw: fact.gmLw,
      impressionsLw: fact.impLw,
      impressionsSince: fact.impSince,
    }))
    .sort(
      (a, b) =>
        (a.days ?? 99999) - (b.days ?? 99999) ||
        compareText(a.planned, b.planned) ||
        compareText(a.chain, b.chain) ||
        compareText(a.property, b.property) ||
        compareText(a.country, b.country) ||
        compareText(a.latestId, b.latestId),
    );
}

export function mpvDiff(vendorMpv: number | null, countryGm: number, countryViews: number): number | null {
  const country = ratio(countryGm, countryViews);
  if (vendorMpv === null || country === null || country === 0) return null;
  return (vendorMpv - country) / country;
}

export function worstLpc(facts: ReadonlyArray<LpcFact>, scope: LpcScope, views: Range): WorstLpcRow[] {
  const grouped = new Map<string, Parts & { vendor: string | null; country: string | null; latestId: string | null; planned: string | null; countryLpcGm: number; countryLpcViews: number }>();
  facts.forEach((fact) => {
    if (fact.product !== DEFAULT_PRODUCT) return;
    if (!matches(scope.country, fact.country)) return;
    const key = [fact.vendor, fact.country, fact.latestId, fact.planned].join('|');
    const acc = grouped.get(key) ?? {
      gmLw: 0, viewsLw: 0, gmP4: 0, viewsP4: null, gmMpv: 0, viewsMpv: 0,
      vendor: fact.vendor, country: fact.country, latestId: fact.latestId, planned: fact.planned,
      countryLpcGm: fact.countryLpcGm, countryLpcViews: fact.countryLpcViews,
    };
    acc.gmLw += fact.gmLw;
    acc.viewsLw += fact.viewsLw;
    acc.gmP4 += fact.gmP4;
    acc.viewsP4 = addNullable(acc.viewsP4, fact.viewsP4);
    acc.gmMpv += fact.gmMpv;
    acc.viewsMpv += fact.viewsMpv;
    grouped.set(key, acc);
  });
  const rows = [...grouped.values()]
    .map((fact) => {
      const metrics = lpcMetrics(fact);
      return {
        vendor: fact.vendor,
        country: fact.country,
        latestId: fact.latestId,
        planned: fact.planned,
        mpvLw: metrics.mpvLw,
        mpvVs: metrics.mpvVs,
        mpvDiff: mpvDiff(metrics.mpvLw, fact.countryLpcGm, fact.countryLpcViews),
        viewsLw: fact.viewsLw,
        viewsVs: metrics.viewsVs,
        gmLw: fact.gmLw,
        gmVs: metrics.gmVs,
      };
    })
    .filter((row) => inRange(row.viewsLw, views));
  return sortByOuterMeasure(
    rows,
    (row) => row.vendor,
    (row) => row.gmVs,
    (a, b) => compareText(a.country, b.country) || compareText(a.latestId, b.latestId) || compareText(a.planned, b.planned),
  );
}

export function worstSpp(facts: ReadonlyArray<SppFact>, scope: SppScope, impressions: Range): WorstSppRow[] {
  const grouped = new Map<string, SppAcc & { property: string | null; country: string | null; latestId: string | null; planned: string | null }>();
  facts.forEach((fact) => {
    if (!sppVisible(fact, { ...scope, eligibleOnly: false })) return;
    const key = [fact.property, fact.country, fact.latestId, fact.planned].join('|');
    const acc = grouped.get(key) ?? { ...emptySpp(), property: fact.property, country: fact.country, latestId: fact.latestId, planned: fact.planned };
    addSpp(acc, fact, scope.margin);
    grouped.set(key, acc);
  });
  const rows = [...grouped.values()]
    .map((fact) => {
      const metrics = sppChannelMetrics(fact);
      return {
        property: fact.property,
        country: fact.country,
        latestId: fact.latestId,
        planned: fact.planned,
        mppoiLw: ratio(fact.marginLw, fact.impLw),
        mppoiVs: metrics.mppoiVs,
        impressionsLw: fact.impLw,
        impVs: metrics.impVs,
        gmLw: fact.gmLw,
        gmVs: metrics.marginVs,
      };
    })
    .filter((row) => inRange(row.impressionsLw, impressions));
  return sortByOuterMeasure(
    rows,
    (row) => row.property,
    (row) => row.gmVs,
    (a, b) => compareText(a.country, b.country) || compareText(a.latestId, b.latestId) || compareText(a.planned, b.planned),
  );
}

function parentByVendor(facts: ReadonlyArray<LpcFact>): Map<string, string | null> {
  const map = new Map<string, string | null>();
  facts.forEach((fact) => {
    const key = `${fact.vendor ?? ''}|${fact.country ?? ''}`;
    if (!map.has(key)) map.set(key, fact.parent);
  });
  return map;
}

export function lpcChart(weeks: ReadonlyArray<LpcWeek>, facts: ReadonlyArray<LpcFact>, scope: LpcScope, metric: 'mpv' | 'views' | 'gm'): Series[] {
  const parents = parentByVendor(facts);
  const products = scope.action ? [DEFAULT_PRODUCT] : [DEFAULT_PRODUCT, LTLE];
  const kept = weeks.filter((week) => {
    if (!week.product || !products.includes(week.product)) return false;
    if (!matches(scope.country, week.country)) return false;
    if (!matches(scope.vendor, week.vendor)) return false;
    const parent = parents.get(`${week.vendor ?? ''}|${week.country ?? ''}`) ?? null;
    if (!matches(scope.chain, chainName(parent))) return false;
    if (scope.action) {
      const split = splitOf(scope.split, week.vendor, week.country, parent);
      if (split !== scope.action) return false;
    }
    return true;
  });
  return products.map((product) => ({
    name: product === DEFAULT_PRODUCT ? 'LPC (Non-SPP)' : 'LTLE',
    points: rollWeeks(
      kept.filter((week) => week.product === product),
      (slice) => {
        const gm = slice.reduce((sum, week) => sum + week.gm, 0);
        const views = slice.reduce((sum, week) => sum + week.views, 0);
        if (metric === 'views') return views;
        if (metric === 'gm') return product === DEFAULT_PRODUCT ? Math.trunc(gm) : gm;
        return ratio(gm, views);
      },
    ),
  }));
}

function sppKeys(facts: ReadonlyArray<SppFact>, scope: SppScope): { keys: Set<string>; parents: Map<string, string | null> } {
  const keys = new Set<string>();
  const parents = new Map<string, string | null>();
  facts.forEach((fact) => {
    if (!sppVisible(fact, { ...scope, eligibleOnly: false, brand: null })) return;
    const key = `${fact.property ?? ''}|${fact.channel ?? ''}|${fact.country ?? ''}`;
    if (scope.action) {
      const split = splitOf(scope.split, fact.property, fact.country, fact.parent);
      if (split !== scope.action) return;
    }
    keys.add(key);
    parents.set(key, fact.parent);
  });
  return { keys, parents };
}

export function sppChart(
  weeks: ReadonlyArray<SppWeek>,
  facts: ReadonlyArray<SppFact>,
  lpcWeeks: ReadonlyArray<LpcWeek>,
  scope: SppScope,
  metric: 'mpi' | 'impressions' | 'views' | 'gm',
): Series[] {
  const { keys } = sppKeys(facts, scope);
  const channelChoice = metric === 'views' ? intersect(scope.channel, VIEWS_SPP_CHANNEL) : scope.channel;
  const kept = weeks.filter((week) => {
    if (!keys.has(`${week.property ?? ''}|${week.channel ?? ''}|${week.country ?? ''}`)) return false;
    return matches(channelChoice, week.channel);
  });
  const channels = [...new Set(kept.map((week) => week.channel).filter((channel): channel is string => channel !== null))].sort((a, b) => a.localeCompare(b));
  const series = channels.map((channel) => ({
    name: channel,
    points: rollWeeks(
      kept.filter((week) => week.channel === channel),
      (slice) => {
        const gm = slice.reduce((sum, week) => sum + (scope.margin === 'Exclude' ? week.gm - week.gmMax : week.gm), 0);
        const impressions = slice.reduce((sum, week) => sum + week.impressions, 0);
        const views = slice.reduce((sum, week) => sum + week.views, 0);
        if (metric === 'impressions') return impressions;
        if (metric === 'views') return views;
        if (metric === 'gm') return gm;
        return ratio(gm, impressions);
      },
    ),
  }));
  if (metric === 'views' || metric === 'gm') {
    const ltle = lpcWeeks.filter((week) => week.product === LTLE && matches(scope.country, week.country));
    series.push({
      name: 'LTLE',
      points: rollWeeks(ltle, (slice) => {
        const gm = slice.reduce((sum, week) => sum + week.gm, 0);
        const views = slice.reduce((sum, week) => sum + week.views, 0);
        return metric === 'views' ? views : gm;
      }),
    });
  }
  return series;
}

function intersect(choice: Choice, required: string): Choice {
  if (choice === 'all') return [required];
  return choice.includes(required) ? [required] : [];
}

function rollWeeks<T extends { week: string }>(rows: T[], value: (slice: T[]) => number | null): Point[] {
  const groups = new Map<string, T[]>();
  rows.forEach((row) => {
    const slice = groups.get(row.week) ?? [];
    slice.push(row);
    groups.set(row.week, slice);
  });
  return [...groups.keys()].sort().map((week) => ({ week, value: value(groups.get(week) ?? []) }));
}

export function money0(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value);
  const body = Math.abs(rounded).toLocaleString('en-AU');
  return rounded < 0 ? `-$${body}` : `$${body}`;
}

export function thousands1(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  const scaled = value / 1000;
  const rounded = Math.round(scaled * 10) / 10;
  const shown = rounded === 0 ? 0 : rounded;
  const body = Math.abs(shown).toLocaleString('en-AU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return shown < 0 ? `-${body}K` : `${body}K`;
}

export function thousands0(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value / 1000);
  const shown = rounded === 0 ? 0 : rounded;
  const body = Math.abs(shown).toLocaleString('en-AU');
  return shown < 0 ? `-${body}K` : `${body}K`;
}

export function money2(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  const body = Math.abs(value).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return value < 0 ? `-$${body}` : `$${body}`;
}

export function pct0(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 100);
  const shown = rounded === 0 ? 0 : rounded;
  return `${shown.toLocaleString('en-AU')}%`;
}

export function integer(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return Math.round(value).toLocaleString('en-AU');
}

/** The Grand Total row comes first, as it does in the on-page table. */
export function lpcUpliftCsv(rows: ReadonlyArray<UpliftRow>, grand: UpliftRow): string {
  return csv(
    [grand, ...rows].map((row) => ({
      'Data Split': row.split,
      'MpV vs P4W': row.mpvVs,
      'Views vs P4W': row.viewsVs,
      'GM vs P4W': row.gmVs,
      'GM LW': row.gmLw,
    })),
    ['Data Split', 'MpV vs P4W', 'Views vs P4W', 'GM vs P4W', 'GM LW'],
  );
}

/** The Grand Total rows (one per channel) come first, as they do in the on-page table. */
export function sppUpliftCsv(rows: ReadonlyArray<SppUpliftRow>, grand: Record<string, ChannelMetrics>): string {
  const flat = [{ split: 'Grand Total', channels: grand }, ...rows].flatMap((row) =>
    Object.entries(row.channels).map(([channel, cell]) => ({
      'Data Split': row.split,
      Channel: channel,
      'MpPOI vs P4W': cell.mppoiVs,
      'Impres. vs P4W': cell.impVs,
      'Views vs P4W': cell.viewsVs,
      'Margin vs P4W': cell.marginVs,
      'GM LW': cell.gmLw,
    })),
  );
  return csv(flat, ['Data Split', 'Channel', 'MpPOI vs P4W', 'Impres. vs P4W', 'Views vs P4W', 'Margin vs P4W', 'GM LW']);
}

export function recentLpcCsv(rows: ReadonlyArray<RecentLpcRow>): string {
  return csv(
    rows.map((row) => ({
      'Days Since Launch': row.days,
      'Planned Run Date': row.planned,
      'Chain Name': row.chain,
      'Vendor Name': row.vendor,
      'Offer Country': row.country,
      'Latest Id': row.latestId,
      'MpV LW': row.mpvLw,
      'GM LW': row.gmLw,
      'Views LW': row.viewsLw,
      'MpV Since Launch': row.mpvSince,
      'GM Since Launch': row.gmSince,
      'Views Since Launch': row.viewsSince,
    })),
    ['Days Since Launch', 'Planned Run Date', 'Chain Name', 'Vendor Name', 'Offer Country', 'Latest Id', 'MpV LW', 'GM LW', 'Views LW', 'MpV Since Launch', 'GM Since Launch', 'Views Since Launch'],
  );
}

export function recentSppCsv(rows: ReadonlyArray<RecentSppRow>): string {
  return csv(
    rows.map((row) => ({
      'Days Since Launch': row.days,
      'Planned Run Date': row.planned,
      'Chain Name': row.chain,
      'SPP Master Property Name': row.property,
      'Offer Country': row.country,
      'Latest Id': row.latestId,
      'MpPOI LW': row.mppoiLw,
      'GM LW': row.gmLw,
      'Impressions LW': row.impressionsLw,
      'Impressions Since Launch': row.impressionsSince,
    })),
    ['Days Since Launch', 'Planned Run Date', 'Chain Name', 'SPP Master Property Name', 'Offer Country', 'Latest Id', 'MpPOI LW', 'GM LW', 'Impressions LW', 'Impressions Since Launch'],
  );
}

export function worstLpcCsv(rows: ReadonlyArray<WorstLpcRow>): string {
  return csv(
    rows.map((row) => ({
      'Vendor Name': row.vendor,
      'Offer Country': row.country,
      'Latest Id': row.latestId,
      'Planned Run Date': row.planned,
      'MpV LW': row.mpvLw,
      'MpV vs P4W': row.mpvVs,
      'MpV vs Country': row.mpvDiff,
      'Views LW': row.viewsLw,
      'Views vs P4W': row.viewsVs,
      'GM LW': row.gmLw,
      'GM vs P4W': row.gmVs,
    })),
    ['Vendor Name', 'Offer Country', 'Latest Id', 'Planned Run Date', 'MpV LW', 'MpV vs P4W', 'MpV vs Country', 'Views LW', 'Views vs P4W', 'GM LW', 'GM vs P4W'],
  );
}

export function worstSppCsv(rows: ReadonlyArray<WorstSppRow>): string {
  return csv(
    rows.map((row) => ({
      'SPP Master Property Name': row.property,
      'Offer Country': row.country,
      'Latest Id': row.latestId,
      'Planned Run Date': row.planned,
      'MpPOI LW': row.mppoiLw,
      'MpPOI vs P4W': row.mppoiVs,
      'Impressions LW': row.impressionsLw,
      'Impres. vs P4W': row.impVs,
      'GM LW': row.gmLw,
      'GM vs P4W': row.gmVs,
    })),
    ['SPP Master Property Name', 'Offer Country', 'Latest Id', 'Planned Run Date', 'MpPOI LW', 'MpPOI vs P4W', 'Impressions LW', 'Impres. vs P4W', 'GM LW', 'GM vs P4W'],
  );
}

export function distinct(values: Array<string | null>): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))].sort((a, b) => a.localeCompare(b));
}
